import { t } from '../i18n/t';

// Anzeigeformate wie in der Demo. Zeitzone ist immer Europe/Berlin. Wörter (Monate, „Heute“, Einheiten bei plural)
// übersetzt t() in die Sprache der Mandanten-App; in der Kanzlei bleibt alles Deutsch.
const TZ = 'Europe/Berlin';

export const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
export const MONTHS_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

export const monthName = (m: number) => (MONTHS[m - 1] ? t(MONTHS[m - 1]!) : String(m));
export const monthShort = (m: number) => (MONTHS_SHORT[m - 1] ? t(MONTHS_SHORT[m - 1]!) : String(m));

function parts(d: Date) {
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    weekday: 'short',
  }).formatToParts(d);
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return { y: get('year'), m: get('month'), d: get('day'), hh: get('hour'), mm: get('minute') };
}

const toDate = (v: string | Date) => (typeof v === 'string' ? new Date(v) : v);

// „07.10.2026“
export function fmtDate(v: string | Date): string {
  // Reine Datumswerte („2026-10-07“) nicht über Zeitzonen verschieben
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-');
    return `${d}.${m}.${y}`;
  }
  const p = parts(toDate(v));
  return `${p.d}.${p.m}.${p.y}`;
}

// „07.10.“
export function fmtDayMonth(v: string | Date): string {
  return fmtDate(v).slice(0, 6);
}

// Datum in der grauen Zeile einer Liste: „20.10.“ in diesem Jahr, sonst „20.10.2025“ (today: YYYY-MM-DD)
export function kurzTag(v: string, today: string): string {
  const d = fmtDate(v);
  return d.slice(6) === today.slice(0, 4) ? d.slice(0, 6) : d;
}

// „14:05“
export function fmtTime(v: string | Date): string {
  const p = parts(toDate(v));
  return `${p.hh}:${p.mm}`;
}

// „07.10. 14:05“
export function fmtDateTime(v: string | Date): string {
  return `${fmtDayMonth(v)} ${fmtTime(v)}`;
}

// Tagesangabe im Chat: „Heute“, „Gestern“, Wochentag (unter 7 Tagen), sonst „07.10.“ (mit Jahr, wenn nicht dieses Jahr)
export function dayLabel(v: string | Date, now = new Date()): string {
  const d = toDate(v);
  const a = parts(d);
  const n = parts(now);
  const dayNum = (p: { y: string; m: string; d: string }) => Date.UTC(+p.y, +p.m - 1, +p.d) / 86400000;
  const diff = dayNum(n) - dayNum(a);
  if (diff === 0) return t('Heute');
  if (diff === 1) return t('Gestern');
  if (diff > 1 && diff < 7) return t(WEEKDAYS[new Date(Date.UTC(+a.y, +a.m - 1, +a.d)).getUTCDay()]!);
  return a.y === n.y ? `${a.d}.${a.m}.` : `${a.d}.${a.m}.${a.y}`;
}

// Heutiges Datum in Berlin als Jahr/Monat/Tag
export function today(now = new Date()) {
  const p = parts(now);
  return { year: +p.y, month: +p.m, day: +p.d };
}

// Initialen: erster Buchstabe der ersten zwei Wörter, z. B. „MM“
export function initials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0] ?? '')
      .join('')
      .toUpperCase() || '?'
  );
}

// Vorname für die Begrüßung (erstes Wort, Titel wie „Dr.“ werden übersprungen)
export function firstName(name: string): string {
  const words = name.trim().split(/\s+/);
  const w = words.find((x) => !/^(dr|prof|dipl(-\w+)?)\.?(-\w+\.?)?$/i.test(x));
  return w ?? words[0] ?? '';
}

// „A, B und C“
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${t('und')} ${names[names.length - 1]}`;
}

// Text kürzen, am Wortende, mit „ …“
export function shorten(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return `${(sp > max * 0.6 ? cut.slice(0, sp) : cut).trimEnd()} …`;
}

export function fmtKB(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// „1 Beleg“ / „3 Belege“ (die Wörter übersetzt t())
export function plural(n: number, one: string, many: string): string {
  return `${n} ${t(n === 1 ? one : many)}`;
}

// Suche nach Namen (Mitarbeiter, Sam 12.10.): Jedes Wort der Suche muss im Namen vorkommen, Reihenfolge,
// Groß-/Kleinschreibung und Akzente egal („weber jon“ findet „Jonas Weber“, „muller“ findet „Müller“).
const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('de');
export function matchesName(name: string, query: string): boolean {
  const n = fold(name);
  return fold(query)
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => n.includes(w));
}

// Ab so vielen Mitarbeitern gibt es die Suche (bei wenigen wäre sie nur im Weg)
export const SEARCH_FROM = 6;
